type MenuAimOptions = {
  exitDelay?: number; // delay in ms before deactivating menu
  interval?: number; // interval in ms to check mouse movement
  sensitivity?: number; // minimum slowdown to trigger activation
  onEnter: (el: HTMLElement) => void; // callback when entering element
  onExit: (el: HTMLElement) => void; // callback when leaving element
};

export function useMenuAim(elements: any[], userConfig: MenuAimOptions) {
  // default options
  const defaultOptions: Partial<MenuAimOptions> = {
    exitDelay: 400,
    interval: 100,
    sensitivity: 7,
  };

  const config: MenuAimOptions = { ...defaultOptions, ...userConfig } as MenuAimOptions;

  let currX = 0;
  let currY = 0;
  let prevX = 0;
  let prevY = 0;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let exitTimer: ReturnType<typeof setTimeout> | null = null;

  // update mouse position
  const mouseTrack = (ev: MouseEvent) => {
    currX = ev.pageX;
    currY = ev.pageY;
  };

  // check if mouse movement has slowed enough to trigger active state
  const mouseCompare = (targetElem: HTMLElement & { isActive?: boolean }) => {
    const distX = prevX - currX;
    const distY = prevY - currY;
    const distance = Math.sqrt(distX * distX + distY * distY);

    if (distance < (config.sensitivity || 7)) {
      // cancel exit timer and deactivate all active elements
      if (exitTimer) clearTimeout(exitTimer);
      elements.forEach((el) => {
        if (el.isActive) {
          config.onExit(el);
          el.isActive = false;
        }
      });

      // trigger hover
      config.onEnter(targetElem);
      targetElem.isActive = true;
    } else {
      // update previous coordinates and check again after interval
      prevX = currX;
      prevY = currY;
      pollTimer = setTimeout(() => mouseCompare(targetElem), config.interval);
    }
  };

  // initialize menu-aim on all elements
  const init = () => {
    if (!config.onEnter || !config.onExit) {
      throw new Error('onEnter and onExit callbacks must be provided');
    }

    elements.forEach((elem) => {
      // store current element state
      elem.isActive = false;

      // track mouse movement over element
      elem.addEventListener('mousemove', mouseTrack);

      elem.addEventListener('mouseenter', (ev: MouseEvent) => {
        prevX = ev.pageX;
        prevY = ev.pageY;

        // cancel exit timer if already active
        if (elem.isActive) {
          if (exitTimer) clearTimeout(exitTimer);
          return;
        }

        // start polling mouse movement
        pollTimer = setTimeout(() => mouseCompare(elem), config.interval);
      });

      elem.addEventListener('mouseleave', () => {
        if (pollTimer) clearTimeout(pollTimer);
        if (!elem.isActive) return;

        // delay exit to allow diagonal movement towards submenu
        exitTimer = setTimeout(() => {
          config.onExit(elem);
          elem.isActive = false;
        }, config.exitDelay);
      });
    });
  };

  init();

  // cleanup function for Composition API
  const destroy = () => {
    elements.forEach((elem) => {
      elem.removeEventListener('mousemove', mouseTrack);
    });
    if (pollTimer) clearTimeout(pollTimer);
    if (exitTimer) clearTimeout(exitTimer);
  };

  return { destroy };
}
